// Three depths of the same assessment.
//
// The results page and the report both used to render one fixed depth: every
// matrix, every control row, every reference appendix, for every reader. That
// makes the same document simultaneously too much for the person approving the
// budget and too little for the person implementing the fix — and "too much
// data" is the fastest way for a finding to be dismissed rather than argued
// with.
//
// So depth is now a single choice that both surfaces honour. It never changes
// what was computed: the score, the stage and the ranking are identical at
// every level. It only changes how much of the working is shown.

export const DETAIL_LEVELS = ["executive", "business", "technical"] as const;

export type DetailLevel = (typeof DETAIL_LEVELS)[number];

export const DEFAULT_DETAIL_LEVEL: DetailLevel = "business";

const RANK: Record<DetailLevel, number> = {
  executive: 0,
  business: 1,
  technical: 2,
};

export const DETAIL_LEVEL_META: Record<
  DetailLevel,
  { label: string; audience: string; blurb: string }
> = {
  executive: {
    label: "Executive",
    audience: "For a VP or board conversation",
    blurb:
      "The score, what it means in a sentence, the biggest risks, and the actions to fund. No matrices.",
  },
  business: {
    label: "Business",
    audience: "For programme and portfolio owners",
    blurb:
      "Adds your architecture, where the exposure sits by domain, the full ranked plan, and the Cisco products that close it.",
  },
  technical: {
    label: "Technical",
    audience: "For the engineers doing the work",
    blurb:
      "Everything: control-by-control scores, per-domain heatmaps, framework coverage, and the reference appendices.",
  },
};

export function isDetailLevel(value: unknown): value is DetailLevel {
  return (
    typeof value === "string" && (DETAIL_LEVELS as readonly string[]).includes(value)
  );
}

/** True when `level` is at least as deep as `min`. */
export function atLeastLevel(level: DetailLevel, min: DetailLevel): boolean {
  return RANK[level] >= RANK[min];
}

// ---------------------------------------------------------------------------
// Report sections
//
// One table, honoured by both renderers (PDF and DOCX), so the two documents
// can't drift into disagreeing about what an "executive" report contains.

export type ReportSectionId =
  | "cover"
  | "executive_summary"
  | "scope"
  | "architecture"
  | "entity_inventory"
  | "top_threats"
  | "threat_heatmap"
  | "domain_maturity"
  | "framework_coverage"
  | "focused_plan"
  | "recommendations"
  | "product_exposure"
  | "per_use_case"
  | "threat_definitions"
  | "control_definitions"
  | "frameworks_referenced"
  | "methodology";

/** The shallowest level at which each section appears. */
export const REPORT_SECTION_MIN_LEVEL: Record<ReportSectionId, DetailLevel> = {
  cover: "executive",
  executive_summary: "executive",
  scope: "executive",
  top_threats: "executive",
  focused_plan: "executive",
  recommendations: "executive",

  architecture: "business",
  entity_inventory: "business",
  threat_heatmap: "business",
  domain_maturity: "business",
  product_exposure: "business",
  methodology: "business",

  framework_coverage: "technical",
  per_use_case: "technical",
  threat_definitions: "technical",
  control_definitions: "technical",
  frameworks_referenced: "technical",
};

export function includeReportSection(
  id: ReportSectionId,
  level: DetailLevel = DEFAULT_DETAIL_LEVEL,
): boolean {
  return atLeastLevel(level, REPORT_SECTION_MIN_LEVEL[id]);
}
